import * as React from 'react';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { CoachStateProvider, useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import { TwoPanelChatRouteProvider } from 'bundles/unified-common/providers/TwoPanelChatRouteProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';

export type TwoPanelConversationalDiscoveryExperimentContextType = {
  twoPanelConversationalDiscoveryEnabled: boolean;
};

export const TwoPanelConversationalDiscoveryExperimentContext =
  createContext<TwoPanelConversationalDiscoveryExperimentContextType>({
    twoPanelConversationalDiscoveryEnabled: false,
  });

// useLayoutEffect warns during SSR; useEffect is inert there and keeps the server render silent.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Opens the chat during CSR once the viewport measures ≥1024px, and closes it below.
 * coachOpen starts false and only ever controls the chat *content* — the panel container
 * (aside + grid track) is governed by useTwoPanelPanelVisibility below, so the chat/session
 * never starts on viewports where the panel is hidden, and the career-fit surfaces (which
 * share this coachOpen) start closed below 1024px instead of auto-opening.
 */
const CoachOpenViewportSync = () => {
  const isDesktop = useIsDesktop();
  const { setCoachOpen } = useCoachState();

  // Layout effect (not passive): the viewport measurement that closes the chat below 1024px is
  // the same one that flips the career-fit surfaces on — a paint in between would flash the
  // legacy chat open with the inherited coachOpen=true before this sync closes it.
  useIsomorphicLayoutEffect(() => {
    setCoachOpen(isDesktop);
  }, [isDesktop, setCoachOpen]);

  return null;
};

export const TwoPanelPanelVisibilityContext = createContext(false);

/**
 * Whether the docked chat panel container (the aside and its reserved grid track) should be
 * rendered. True on the server and through hydration — the panel container and its skeleton are
 * part of the SSR HTML, so desktop-width first paints are already two-panel even though
 * coachOpen starts false. After the first client viewport measurement it follows coachOpen
 * (opened ≥1024px by CoachOpenViewportSync) gated on the desktop check, so a career-fit chat
 * opening below 1024px never resurrects the hidden panel container.
 */
const PanelVisibilityProvider = ({ children }: React.PropsWithChildren) => {
  const { coachOpen } = useCoachState();
  const isDesktop = useIsDesktop();
  const [viewportMeasured, setViewportMeasured] = useState(false);

  // Layout effect so it lands in the same pre-paint flush as CoachOpenViewportSync — the panel
  // container never paints in a state that disagrees with the measured viewport.
  useIsomorphicLayoutEffect(() => {
    setViewportMeasured(true);
  }, []);

  const panelVisible = (coachOpen && isDesktop) || !viewportMeasured;

  return (
    <TwoPanelPanelVisibilityContext.Provider value={panelVisible}>{children}</TwoPanelPanelVisibilityContext.Provider>
  );
};

export const useTwoPanelPanelVisibility = () => useContext(TwoPanelPanelVisibilityContext);

function TwoPanelConversationalDiscoveryProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  // The two-panel conversational discovery treatment (chat docked on the right) is
  // now the permanent consumer XDP experience. Eligibility is UA-only:
  // the UA is fixed for the session, so this can never flip between SSR/hydration and later
  // client renders — a flip would reparent (unmount/remount) the whole page subtree below.
  // Sub-1024px desktop viewports keep the treatment tree; the panel is handled by
  // breakpoint-scoped CSS plus useTwoPanelPanelVisibility/CoachOpenViewportSync below.
  const userAgent = useUserAgent();
  const isMobileOrTabletUserAgent = userAgent?.isMobileBrowser || userAgent?.isMobile;

  const value = useMemo(
    () => ({
      twoPanelConversationalDiscoveryEnabled: !isMobileOrTabletUserAgent,
    }),
    [isMobileOrTabletUserAgent]
  );

  return (
    <TwoPanelConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.twoPanelConversationalDiscoveryEnabled ? (
        <CoachStateProvider coachOpen={false} isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}>
          <CoachOpenViewportSync />
          <PanelVisibilityProvider>
            <TwoPanelChatRouteProvider>{children}</TwoPanelChatRouteProvider>
          </PanelVisibilityProvider>
        </CoachStateProvider>
      ) : (
        children
      )}
    </TwoPanelConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useTwoPanelConversationalDiscoveryExperiment = () => {
  return useContext(TwoPanelConversationalDiscoveryExperimentContext);
};

export default TwoPanelConversationalDiscoveryProvider;
